import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'

import { useSnackbar } from 'notistack'
import { QueryParams } from '../../common/types'
import { getErrorMessage, parseQueryParams } from '../../utilities/parsers'
import apiUrl from '../../apis/api.url'
import {
  deleteData,
  getData,
  postData,
  updateData,
  updateFromData,
} from '../../apis/api.helpers'
import { useSnackbarManager } from '../../components/common/snackbar'
import { useAuthStore } from '../../store/authStore'

const buildUrlWithParams = (baseUrl: string, params: QueryParams) => {
  return `${baseUrl}${parseQueryParams(params)}`
}

const fetchData = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.ADMIN_INSTITUTIONS, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useAdminUser = (input: QueryParams) => {
  return useQuery(['admin_user_list', input], () => fetchData(input), {})
}

const customerMode = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.CUSTOMER_MODE, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useCustomerMode = (input: QueryParams) => {
  return useQuery(['customer_mode', input], () => customerMode(input), {})
}
export const deleteAdmin = (id?: string) => {
  return deleteData(`${apiUrl.ADMIN_INSTITUTIONS}/${id}`)
}

const adminDetails = async (id: string) => {
  const url = `${apiUrl.ADMIN_INSTITUTIONS}/${id}`
  const response = await getData(url)
  return response
}
export const useAdminDetails = (id: string) => {
  return useQuery(['worker_task', id], () => adminDetails(id), {
    refetchOnWindowFocus: false,
  })
}

export const createIncubation = (input: any) => {
  return postData(`${apiUrl.INCUBATION_CREATE}`, input)
}
export const useCreateIncubation = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const authenticated = useAuthStore((state) => state.authenticated)

  return useMutation({
    mutationFn: async (input: any) => {
      if (!authenticated) throw new Error('Not authenticated')
      return createIncubation(input)
    },
    onSuccess: (res: any) => {
      onSuccessHandler(res.data)
      enqueueSnackbar('Academy created successfully.', { variant: 'success' })
    },
    onError: (err: any) => {
      enqueueSnackbar(
        getErrorMessage(err?.response?.data?.message || err?.message),
        {
          variant: 'error',
        }
      )
    },
  })
}
export const createCv = (input: any, id: any) => {
  console.log(input)
  return postData(`${apiUrl.INCUBATION_CREATE}/${id}/upload-pitch-deck`, input)
}
export const useCreateCv = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const authenticated = useAuthStore((state) => state.authenticated)

  return useMutation({
    mutationFn: async ({ formData, id }: { formData: any; id: any }) => {
      if (!authenticated) throw new Error('Not authenticated')
      return createCv(formData, id)
    },
    onSuccess: (res: any) => {
      onSuccessHandler(res.data)
      enqueueSnackbar('CV uploaded successfully.', { variant: 'success' })
    },
    onError: (err: any) => {
      enqueueSnackbar(
        getErrorMessage(err?.response?.data?.message || err?.message),
        {
          variant: 'error',
        }
      )
    },
  })
}

const fetchUserDetails = async () => {
  const response = await getData(apiUrl.FETCH_INITIAL_PROFILE) // Replace with actual endpoint
  return response
}

export const useFetchUserDetails = () => {
  return useQuery(['user_details'], fetchUserDetails)
}

export const assignTemplate = (input: any) => {
  return postData(`${apiUrl.TEMPLATE_ASSIGN}`, input)
}
export const useAssignTemplate = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(assignTemplate, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)
      enqueueSnackbar('Template Assigned Successfully', { variant: 'success' })
    },

    onError: (error: any) => {
      enqueueSnackbar(
        getErrorMessage(
          error.response.data.error || error?.response?.data?.detail
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}

export const editCategory = (data: { id: number; input: any }) => {
  return updateData(`${apiUrl.ADMIN_INSTITUTIONS}/${data.id}`, {
    user: data.input,
  })
}

export const useUpdateAdmin = (
  handleSubmission: (data: any) => void // Simplify this if `id` isn't needed separately
) => {
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(editCategory, {
    onSuccess: (res: any) => {
      handleSubmission(res.data)

      enqueueSnackbar('Customer Edited Successfully', { variant: 'success' })
    },
    onError: (error: any) => {
      enqueueSnackbar(getErrorMessage(error?.response?.data?.message), {
        variant: 'error',
      })
    },
  })
}

export const updatePassword = (employee: string, data: string) => {
  return updateFromData(
    `${apiUrl.ADMIN_USER}/${employee}/change_password`,
    data
  )
}

export const sendAdminInvitation = (id?: string) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/invite`, {})
}

// Role change API functions
export const changeUserRole = (input: any) => {
  return postData(apiUrl.CHANGE_ROLE, input)
}

export const useChangeUserRole = (onSuccessHandler: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  const { clearAuthenticated } = useAuthStore()

  return useMutation({
    mutationFn: changeUserRole,
    onSuccess: (res: any) => {
      onSuccessHandler(res)
      enqueueSnackbar('Role changed successfully. You will be logged out.', {
        variant: 'success',
      })
      // Clear authentication and redirect after a brief delay
      setTimeout(() => {
        clearAuthenticated()
        window.location.href = '/login'
      }, 2000)
    },
    onError: (err: any) => {
      enqueueSnackbar(
        getErrorMessage(
          err?.response?.data?.message || err?.message || 'Role change failed'
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}
